import { Content, ContentType } from 'src/app/models/documents/folder.model';
import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveItem } from './drive-viewer.models';

export class DriveTreeMapper {
  constructor(private readonly presentation: DriveItemPresentationPolicy) {}

  flatten(root: Content): DriveItem[] {
    const target: DriveItem[] = [];
    this.flattenNode(root, null, [], target);
    return target;
  }

  flattenNode(node: Content, parentId: string | null, parentPath: string[], target: DriveItem[]): void {
    if (!node) return;
    const id = String(node.id);
    const name = node.name || '';
    const modified = node.createdAt || new Date().toISOString();
    const path = [...parentPath, name];
    const children = Array.isArray(node.children) ? node.children : [];
    const kind = this.inferKind(node, children);
    if (kind === 'folder') {
      target.push({ id, name, type: 'folder', owner: 'you', modified, size: 0, path, parentId });
      children.forEach((child) => this.flattenNode(child, id, path, target));
      return;
    }
    const rawSize = (node as Content & { size?: number | string }).size;
    const size = typeof rawSize === 'number' ? rawSize : Number.parseInt(rawSize || '0', 10) || 0;
    target.push({
      id,
      name,
      type: this.presentation.typeByExtension(name),
      owner: 'you',
      modified,
      size,
      path: parentPath,
      parentId,
      documentId: this.resolveDocumentId(node, id)
    });
  }

  inferKind(node: Content, children: Content[]): 'folder' | 'file' {
    const rawType = String(node.type ?? '').trim().toLowerCase();
    const name = String(node.name ?? '');
    const hasFileName = this.presentation.typeByExtension(name) !== 'other' || /\.[a-z0-9]{1,12}$/i.test(name);
    const hasDocumentIdentity = !!this.resolveDocumentId(node, '');
    const isFolderType = rawType === String(ContentType.FOLDER) || rawType === 'folder';
    const isFileType = rawType === String(ContentType.FILE) || rawType === 'file';
    if (isFileType || hasDocumentIdentity || (hasFileName && children.length === 0)) return 'file';
    if (isFolderType || children.length > 0) return 'folder';
    return hasFileName ? 'file' : 'folder';
  }

  resolveDocumentId(node: Content, fallbackId: string): string | undefined {
    const source = node as Content & {
      documentId?: unknown;
      document_id?: unknown;
      documentID?: unknown;
      document?: { id?: unknown };
    };
    const raw = source.documentId ?? source.document_id ?? source.documentID ?? source.document?.id ?? fallbackId;
    const value = raw == null ? '' : String(raw).trim();
    return value || undefined;
  }
}
